import React from 'react';

import { mdiEmoticonOutline } from '@lumapps/lumx/icons';
import { EmojiPickerDialog } from '@lumapps/pickers/components/EmojiPickerDialog';
import { EmojiDetails } from '@lumapps/pickers/types';
import { useTranslate } from '@lumapps/translations';
import { useBooleanState } from '@lumapps/utils/hooks/useBooleanState';
import { TypographyEditor } from '@lumapps/wrex-typography/types';
import { ReactEditor, Transforms, useSlateStatic } from '@lumapps/wrex/slate';
import { initSelection } from '@lumapps/wrex/slate/utils/initSelection';
import { ToolbarItem, WrexEditor } from '@lumapps/wrex/types';

import { WREX_EMOJI } from '../../../keys';
import { EmojiEditor } from '../../../types';

export const useEmojiButton = (otherProps?: any) => {
    const editorStatic = useSlateStatic() as WrexEditor<EmojiEditor & TypographyEditor>;
    const { translateKey } = useTranslate();

    const [isEmojiPickerOpen, , closeEmojiPicker, openEmojiPicker] = useBooleanState(false);
    const currentAnchorRef = React.useRef<HTMLElement | null>(null);
    const shouldFocusButton = React.useRef(true);
    const isDisabled = !editorStatic.isEmojiAllowed();

    const emojiButton = React.useMemo(() => {
        const onInsertEmoji = (emojiObject: EmojiDetails) => {
            Transforms.insertText(editorStatic, emojiObject.unicode || emojiObject.emoji.unicode);
            // IMPORTANT: Focus must happen BEFORE closeEmojiPicker().
            // Closing triggers a state update that causes a re-render, which could reset the cursor position.
            // Focusing first ensures the cursor stays at the emoji insertion point. No setTimeout needed - synchronous
            // focus before state update is sufficient because React batches the state update for the next render.
            ReactEditor.focus(editorStatic);
            shouldFocusButton.current = false; // Don't focus button after emoji insert
            closeEmojiPicker();
            // Note: Don't reset shouldFocusButton here - if PopoverDialog calls onClose
            // during the isOpen transition, it would steal focus from the editor.
            // Reset happens in onCancelEmojiPicker after all focus logic completes.
        };

        const onEmojiButtonSelect = (event?: React.MouseEvent) => {
            if (!editorStatic.selection) {
                initSelection(editorStatic, 'inline');
            }
            // Capture the actual button element
            if (event?.currentTarget) {
                // Mouse click: use event target
                currentAnchorRef.current = event.currentTarget as HTMLElement;
            } else if (document.activeElement instanceof HTMLElement) {
                // Keyboard activation: use currently focused element (the button)
                currentAnchorRef.current = document.activeElement;
            }
            openEmojiPicker();
        };

        const onCancelEmojiPicker = () => {
            closeEmojiPicker();
            // Focus the button manually since we disabled focusAnchorOnClose
            // But only if user explicitly canceled (not after emoji insert)
            if (shouldFocusButton.current && currentAnchorRef.current) {
                currentAnchorRef.current.focus();
            }
            // Reset for next time
            shouldFocusButton.current = true;
        };

        const insertEmojiPicker = (
            <EmojiPickerDialog
                isOpen={isEmojiPickerOpen}
                onEmojiClick={onInsertEmoji}
                onClose={onCancelEmojiPicker}
                anchorRef={currentAnchorRef}
            />
        );

        const emojiButtonProps: ToolbarItem = {
            itemKey: 'emoji',
            type: 'option',
            icon: mdiEmoticonOutline,
            label: translateKey(WREX_EMOJI.EMOJI_BUTTON),
            tooltipLabel: translateKey(WREX_EMOJI.EMOJI_TOOLTIP),
            variant: 'icon',
            'aria-disabled': isDisabled,
            onClick: onEmojiButtonSelect,
            otherProps,
        };

        return { insertEmojiPicker, emojiButtonProps };
    }, [closeEmojiPicker, editorStatic, isDisabled, isEmojiPickerOpen, openEmojiPicker, otherProps, translateKey]);

    return emojiButton;
};
